/* eslint-disable @typescript-eslint/no-unused-vars, no-unused-vars */
import { createRef } from 'react';
import { DSButtonV3 } from '../index.js';
import type { DSButtonV3T } from '../index.js';

// test we expose the namespace and the namespace follows our deliverable conventions
type ComponentPropsForApp = DSButtonV3T.Props;
type ComponentPropsInternals = DSButtonV3T.InternalProps;
type ComponentPropsDefaultProps = DSButtonV3T.DefaultProps;
type ComponentPropsOptionalProps = DSButtonV3T.OptionalProps;
type ComponentPropsRequiredProps = DSButtonV3T.RequiredProps;

const ref = createRef<HTMLButtonElement>();

const testRequiredProps: ComponentPropsRequiredProps = {
  children: 'Button Label',
};
const testOptionalProps: ComponentPropsOptionalProps = {
  innerRef: ref,
};

// difference Props and InternalProps is that InternalProps has all the default props filled in
// Props allows for partial defaults
const testPartialDefaults: Partial<ComponentPropsDefaultProps> = {
  'data-testid': 'ds-button',
  buttonType: 'filled',
  onClick: () => null,
};

const testProps: ComponentPropsForApp = {
  ...testRequiredProps,
  ...testOptionalProps,
  ...testPartialDefaults,
};

const testPropsAsSyntax = {
  ...testRequiredProps,
  ...testOptionalProps,
  ...testPartialDefaults,
} as ComponentPropsForApp;

// InternalProps requires all defaults to be filled in
const testCompleteDefaults: Required<ComponentPropsDefaultProps> = {
  'data-testid': 'ds-button',
  size: 'm',
  type: 'button',
  buttonType: 'filled',
  onClick: () => null,
  shape: 'default',
  applyAriaDisabled: false,
  readOnly: false,
};

const testInternalProps: ComponentPropsInternals = {
  ...testRequiredProps,
  ...testOptionalProps,
  ...testCompleteDefaults,
};

const testInternalPropsAsSyntax = {
  ...testRequiredProps,
  ...testOptionalProps,
  ...testCompleteDefaults,
} as ComponentPropsInternals;

// using the explicit type definition, if there is an error, it will be marked on the key that is wrong
const testExplicitDefinition: ComponentPropsForApp = {
  'data-testid': 'ds-button',
  size: 'm',
  type: 'button',
  buttonType: 'filled',
  onClick: () => null,
  shape: 'default',
  children: 'Button Label',
  innerRef: ref,
  applyAriaDisabled: false,
};

// using the "as" syntax, if there is an error, it will be marking the whole object as wrong because it is not compatible with the type
const testInferedTypeCompatibility = {
  'data-testid': 'ds-button',
  size: 'm',
  type: 'button',
  buttonType: 'filled',
  onClick: () => null,
  shape: 'default',
  children: 'Button Label',
  innerRef: ref,
  applyAriaDisabled: false,
} as ComponentPropsForApp;

const testDefinitionAsConst = {
  'data-testid': 'ds-button',
  size: 'm',
  type: 'button',
  buttonType: 'filled',
  onClick: () => null,
  shape: 'default',
  children: 'Button Label',
  innerRef: ref,
  applyAriaDisabled: false,
} as const;

const ExampleUsageComponent = () => (
  <>
    {/* works with explicitly casted props, all syntaxes */}
    <DSButtonV3 {...testExplicitDefinition} />
    <DSButtonV3 {...testInferedTypeCompatibility} />
    <DSButtonV3 {...testDefinitionAsConst} />
    {/* works with inline values */}
    <DSButtonV3
      data-testid="ds-button"
      size="m"
      type="button"
      buttonType="filled"
      onClick={() => null}
      shape="default"
      innerRef={ref}
      applyAriaDisabled={false}
      readOnly={false}
    >
      Button Label
    </DSButtonV3>
  </>
);
